import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articlesMock } from '@/mocks/home';
import JsonLd from '@/components/base/JsonLd';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articlesMock.find((a) => a.id === Number(id));

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 开云棋牌`;
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/articles" className="text-primary-500 hover:text-primary-600 font-medium">
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articlesMock
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const contentParagraphs = article.content.split('\n\n').filter(Boolean);

  const articleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '开云棋牌',
    },
    publisher: {
      '@type': 'Organization',
      name: '开云棋牌',
      url: 'https://kaiyunqipai.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://kaiyunqipai.com/articles/${article.id}`,
    },
  } : null;

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-18">
        {articleSchema && <JsonLd data={articleSchema} />}
        <nav className="bg-background-50 border-b border-background-200" aria-label="面包屑导航">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <ol className="flex items-center gap-1.5 py-3 text-xs md:text-sm flex-wrap">
              <li>
                <Link to="/" className="text-foreground-400 hover:text-primary-500 transition-colors">
                  首页
                </Link>
              </li>
              <li className="text-foreground-300">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li>
                <Link to="/articles" className="text-foreground-400 hover:text-primary-500 transition-colors">
                  游戏资讯
                </Link>
              </li>
              <li className="text-foreground-300">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li className="text-foreground-700 font-medium truncate max-w-[200px]">
                {article.title}
              </li>
            </ol>
          </div>
        </nav>

        <article className="py-10 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <header className="mb-8 md:mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium">
                  {article.category}
                </span>
                <span className="text-foreground-400 text-xs flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="text-foreground-400 text-xs flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读 {article.readTime}
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading leading-tight">
                {article.title}
              </h1>
            </header>

            <div className="relative h-64 md:h-80 lg:h-96 rounded-xl overflow-hidden mb-8 md:mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose prose-lg max-w-none text-foreground-700">
              {contentParagraphs.map((paragraph, idx) => {
                if (paragraph.startsWith('## ')) {
                  return (
                    <h2
                      key={idx}
                      className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mt-8 mb-4"
                      id={`section-${idx}`}
                    >
                      <a href={`#section-${idx}`} className="no-underline">
                        {paragraph.replace('## ', '')}
                      </a>
                    </h2>
                  );
                }
                return (
                  <p
                    key={idx}
                    className="text-foreground-600 leading-relaxed mb-4 text-sm md:text-base"
                  >
                    {paragraph}
                  </p>
                );
              })}
            </div>

            <footer className="mt-10 md:mt-12 pt-6 md:pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="text-sm text-foreground-400">标签：</span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">
                  开云棋牌
                </span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">
                  {article.category}
                </span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">
                  棋牌游戏
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to="/articles"
                  className="flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium text-sm transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回文章列表
                </Link>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-400">分享到：</span>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer">
                    <i className="ri-wechat-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer">
                    <i className="ri-weibo-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer">
                    <i className="ri-link text-sm"></i>
                  </button>
                </div>
              </div>
            </footer>

            {relatedArticles.length > 0 && (
              <aside className="mt-10 md:mt-14">
                <h3 className="text-lg md:text-xl font-bold text-foreground-900 font-heading mb-5">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/articles/${ra.id}`}
                      className="group bg-background-100 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3 md:p-4">
                        <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {ra.title}
                        </h4>
                        <span className="text-xs text-foreground-400 mt-2 block">{ra.date}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </aside>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}